
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import { getOptimizedTutorCardUrl } from '@/utils/imageOptimization';

interface RandomTutorCardProps {
  tutor: {
    id: string;
    name: string;
    profilePicture?: string;
    expand?: {
      user?: {
        name?: string;
      };
    };
  };
}

const RandomTutorCard = ({ tutor }: RandomTutorCardProps) => {
  const navigate = useNavigate();

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Unknown Tutor';
  };

  const getProfilePictureUrl = (tutor: any) => {
    if (tutor.profilePicture && tutor.profilePicture.trim()) {
      // Use optimized 200x200 thumbnail for tutor cards
      return getOptimizedTutorCardUrl(tutor, tutor.profilePicture);
    }
    if (tutor.profilePictureUrl && tutor.profilePictureUrl.trim()) {
      return tutor.profilePictureUrl;
    }
    return '';
  };

  const handleClick = () => {
    navigate(`/tutor/${tutor.id}`);
  };

  const tutorName = getTutorName(tutor);
  const profilePictureUrl = getProfilePictureUrl(tutor);

  return (
    <div 
      className="relative overflow-hidden rounded-lg cursor-pointer group transition-all duration-300 hover:shadow-lg aspect-square"
      onClick={handleClick}
    >
      {/* Full-size image */}
      {profilePictureUrl ? (
        <img
          src={profilePictureUrl}
          alt={tutorName}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
          <div className="text-blue-600 font-bold text-lg md:text-xl lg:text-2xl">
            {tutorName.split(' ').map(n => n[0]).join('').toUpperCase()}
          </div>
        </div>
      )}
      
      {/* Overlay gradient for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/70 transition-all duration-300" />
      
      {/* Name overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-1 md:p-2">
        <h3 className="font-medium text-xs text-white truncate group-hover:text-blue-200 transition-colors duration-300 text-center">
          {tutorName}
        </h3>
      </div>
    </div>
  );
};

export default RandomTutorCard;
